import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Pencil } from 'lucide-react';

import {
  fetchCliente,
  fetchClienteArtefatos,
  fetchClientePeticoesIniciais,
  fetchClienteProcessos,
  type Cliente,
} from '@/api/clientes';
import { type Artefato, type Processo } from '@/api/client';
import type { PeticaoInicialResumo } from '@/api/clientes';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TagsDoRegistro } from '@/components/tags/TagsDoRegistro';
import { TagChips } from '@/components/ui/tag-chip';
import { useTagsNoCache } from '@/hooks/useTagsNoCache';
import { classifyError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { ClienteDadosContent } from './components/ClienteDadosContent';
import { ClienteDocumentosTab } from './components/ClienteDocumentosTab';
import { ClientePeticoesTab } from './components/ClientePeticoesTab';
import { ClienteProcessosTab } from './components/ClienteProcessosTab';
import { ClienteStatusBadge } from './components/ClienteStatusBadge';

// Detalhe e lista do cliente: o mesmo prefixo `clientes`.
const CACHES_DE_CLIENTE = [queryKeys.clientes.all] as const;

function TabLabel({ label, count }: { label: string; count?: number }) {
  return (
    <>
      {label}
      {count != null && count > 0 && (
        <span className="ml-1 opacity-60">({count})</span>
      )}
    </>
  );
}

export default function ClienteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dados';

  const { data: cliente, isLoading, error } = useQuery<Cliente>({
    queryKey: queryKeys.clientes.detail(id!),
    queryFn: () => fetchCliente(id!),
    enabled: !!id,
  });
  // Cliente de colega (visão do escritório): só para ler — sem editar, sem tags, sem vincular.
  const doColega = cliente?.origem === 'compartilhado';

  // Queries para contagem nas tabs (React Query deduplica com as queries internas dos tabs)
  const { data: processos } = useQuery<Processo[]>({
    queryKey: queryKeys.clientes.processos(id!),
    queryFn: () => fetchClienteProcessos(id!),
    enabled: !!id,
  });
  const { data: peticoes } = useQuery<PeticaoInicialResumo[]>({
    queryKey: queryKeys.clientes.peticoesIniciais(id!),
    queryFn: () => fetchClientePeticoesIniciais(id!),
    enabled: !!id,
  });
  const { data: artefatos } = useQuery<Artefato[]>({
    queryKey: queryKeys.clientes.artefatos(id!),
    queryFn: () => fetchClienteArtefatos(id!),
    enabled: !!id,
  });
  const tagsNoCache = useTagsNoCache(CACHES_DE_CLIENTE);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size={32} label="Carregando cliente..." />
      </div>
    );
  }

  if (error || !cliente) {
    return (
      <div className="space-y-4">
        <Link
          to="/app/clientes"
          className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground sm:min-h-0"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar aos clientes
        </Link>
        <ErrorState
          title="Cliente não encontrado"
          description={error ? classifyError(error).message : 'O cliente solicitado não foi encontrado.'}
          actions={[
            { label: 'Voltar aos clientes', variant: 'outline', onClick: () => navigate('/app/clientes') },
          ]}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/app/clientes"
        className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground sm:min-h-0"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar aos clientes
      </Link>

      {/* Card principal */}
      <Card>
        <CardContent className="space-y-6 px-4 pt-4 sm:px-6 sm:pt-6">
          {/* Header. No celular o nome tem a linha dele (antes quebrava em três, espremido
              entre status, tags e "Editar"); o resto desce para a linha de baixo. */}
          <div className="flex min-w-0 items-center gap-x-3 gap-y-1 max-sm:flex-wrap">
            <h1 className="min-w-0 basis-full break-words text-lg font-bold sm:basis-auto sm:text-xl">{cliente.nome}</h1>
            <ClienteStatusBadge status={cliente.status} />
            {doColega ? (
              <>
                <TagChips tags={cliente.tags} />
                {cliente.compartilhado_por && (
                  <span className="text-xs text-muted-foreground">De {cliente.compartilhado_por}</span>
                )}
              </>
            ) : (
              <>
                <TagsDoRegistro
                  recurso="clientes"
                  registroId={cliente.id}
                  identificador={cliente.nome}
                  tags={cliente.tags}
                  noCache={tagsNoCache}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto h-11 shrink-0 sm:h-8"
                  onClick={() => navigate(`/app/clientes/${id}/editar`)}
                >
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Editar
                </Button>
              </>
            )}
          </div>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })}
          >
            {/* Quatro abas em 294px (360px de tela): cada uma da largura do rótulo (colunas
                iguais estouravam com o contador), sem respiro lateral e a 13px no celular. */}
            <TabsList className="w-full [&>button]:flex-auto [&>button]:px-1 [&>button]:text-[13px] sm:[&>button]:px-3 sm:[&>button]:text-sm">
              <TabsTrigger value="dados">Dados</TabsTrigger>
              <TabsTrigger value="processos">
                <TabLabel label="Processos" count={processos?.length} />
              </TabsTrigger>
              <TabsTrigger value="peticoes">
                <TabLabel label="Petições" count={peticoes?.length} />
              </TabsTrigger>
              <TabsTrigger value="documentos">
                <TabLabel label="Documentos" count={artefatos?.length} />
              </TabsTrigger>
            </TabsList>

            <TabsContent value="dados" className="pt-3">
              <ClienteDadosContent
                cliente={cliente}
                onEditStep={doColega ? undefined : (step) => navigate(`/app/clientes/${id}/editar?step=${step}`)}
              />
            </TabsContent>

            <TabsContent value="processos" className="pt-3">
              <ClienteProcessosTab clienteId={cliente.id} somenteLeitura={doColega} />
            </TabsContent>

            <TabsContent value="peticoes" className="pt-3">
              <ClientePeticoesTab clienteId={cliente.id} />
            </TabsContent>

            <TabsContent value="documentos" className="pt-3">
              <ClienteDocumentosTab clienteId={cliente.id} clienteNome={cliente.nome} somenteLeitura={doColega} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
